/**
 * Countries and their dialling codes.
 *
 * Used by the phone-number screen on both platforms, so it lives here rather
 * than in either app.
 *
 * Not exhaustive — it covers the whole of Africa, because that is ZIvapp's
 * first market and a partial list there would be a visible gap, plus the
 * larger markets everywhere else. Adding a country is one line.
 *
 * `dial` is digits only, with no plus, because that is what toE164() expects.
 * Several countries share a dial code (+1 covers the US and Canada, +7 Russia
 * and Kazakhstan), so `code` — the ISO 3166-1 alpha-2 — is the stable key,
 * never `dial`.
 */

export const COUNTRIES = [
  // ---- Africa ----
  { code: 'DZ', dial: '213', name: 'Algeria', flag: '🇩🇿' },
  { code: 'AO', dial: '244', name: 'Angola', flag: '🇦🇴' },
  { code: 'BJ', dial: '229', name: 'Benin', flag: '🇧🇯' },
  { code: 'BW', dial: '267', name: 'Botswana', flag: '🇧🇼' },
  { code: 'BF', dial: '226', name: 'Burkina Faso', flag: '🇧🇫' },
  { code: 'BI', dial: '257', name: 'Burundi', flag: '🇧🇮' },
  { code: 'CM', dial: '237', name: 'Cameroon', flag: '🇨🇲' },
  { code: 'CV', dial: '238', name: 'Cape Verde', flag: '🇨🇻' },
  { code: 'CF', dial: '236', name: 'Central African Republic', flag: '🇨🇫' },
  { code: 'TD', dial: '235', name: 'Chad', flag: '🇹🇩' },
  { code: 'KM', dial: '269', name: 'Comoros', flag: '🇰🇲' },
  { code: 'CG', dial: '242', name: 'Congo', flag: '🇨🇬' },
  { code: 'CD', dial: '243', name: 'Congo (DRC)', flag: '🇨🇩' },
  { code: 'CI', dial: '225', name: "Côte d'Ivoire", flag: '🇨🇮' },
  { code: 'DJ', dial: '253', name: 'Djibouti', flag: '🇩🇯' },
  { code: 'EG', dial: '20',  name: 'Egypt', flag: '🇪🇬' },
  { code: 'GQ', dial: '240', name: 'Equatorial Guinea', flag: '🇬🇶' },
  { code: 'ER', dial: '291', name: 'Eritrea', flag: '🇪🇷' },
  { code: 'SZ', dial: '268', name: 'Eswatini', flag: '🇸🇿' },
  { code: 'ET', dial: '251', name: 'Ethiopia', flag: '🇪🇹' },
  { code: 'GA', dial: '241', name: 'Gabon', flag: '🇬🇦' },
  { code: 'GM', dial: '220', name: 'Gambia', flag: '🇬🇲' },
  { code: 'GH', dial: '233', name: 'Ghana', flag: '🇬🇭' },
  { code: 'GN', dial: '224', name: 'Guinea', flag: '🇬🇳' },
  { code: 'GW', dial: '245', name: 'Guinea-Bissau', flag: '🇬🇼' },
  { code: 'KE', dial: '254', name: 'Kenya', flag: '🇰🇪' },
  { code: 'LS', dial: '266', name: 'Lesotho', flag: '🇱🇸' },
  { code: 'LR', dial: '231', name: 'Liberia', flag: '🇱🇷' },
  { code: 'LY', dial: '218', name: 'Libya', flag: '🇱🇾' },
  { code: 'MG', dial: '261', name: 'Madagascar', flag: '🇲🇬' },
  { code: 'MW', dial: '265', name: 'Malawi', flag: '🇲🇼' },
  { code: 'ML', dial: '223', name: 'Mali', flag: '🇲🇱' },
  { code: 'MR', dial: '222', name: 'Mauritania', flag: '🇲🇷' },
  { code: 'MU', dial: '230', name: 'Mauritius', flag: '🇲🇺' },
  { code: 'MA', dial: '212', name: 'Morocco', flag: '🇲🇦' },
  { code: 'MZ', dial: '258', name: 'Mozambique', flag: '🇲🇿' },
  { code: 'NA', dial: '264', name: 'Namibia', flag: '🇳🇦' },
  { code: 'NE', dial: '227', name: 'Niger', flag: '🇳🇪' },
  { code: 'NG', dial: '234', name: 'Nigeria', flag: '🇳🇬' },
  { code: 'RW', dial: '250', name: 'Rwanda', flag: '🇷🇼' },
  { code: 'ST', dial: '239', name: 'São Tomé and Príncipe', flag: '🇸🇹' },
  { code: 'SN', dial: '221', name: 'Senegal', flag: '🇸🇳' },
  { code: 'SC', dial: '248', name: 'Seychelles', flag: '🇸🇨' },
  { code: 'SL', dial: '232', name: 'Sierra Leone', flag: '🇸🇱' },
  { code: 'SO', dial: '252', name: 'Somalia', flag: '🇸🇴' },
  { code: 'ZA', dial: '27',  name: 'South Africa', flag: '🇿🇦' },
  { code: 'SS', dial: '211', name: 'South Sudan', flag: '🇸🇸' },
  { code: 'SD', dial: '249', name: 'Sudan', flag: '🇸🇩' },
  { code: 'TZ', dial: '255', name: 'Tanzania', flag: '🇹🇿' },
  { code: 'TG', dial: '228', name: 'Togo', flag: '🇹🇬' },
  { code: 'TN', dial: '216', name: 'Tunisia', flag: '🇹🇳' },
  { code: 'UG', dial: '256', name: 'Uganda', flag: '🇺🇬' },
  { code: 'ZM', dial: '260', name: 'Zambia', flag: '🇿🇲' },
  { code: 'ZW', dial: '263', name: 'Zimbabwe', flag: '🇿🇼' },

  // ---- Rest of the world ----
  { code: 'AR', dial: '54',  name: 'Argentina', flag: '🇦🇷' },
  { code: 'AU', dial: '61',  name: 'Australia', flag: '🇦🇺' },
  { code: 'AT', dial: '43',  name: 'Austria', flag: '🇦🇹' },
  { code: 'BD', dial: '880', name: 'Bangladesh', flag: '🇧🇩' },
  { code: 'BE', dial: '32',  name: 'Belgium', flag: '🇧🇪' },
  { code: 'BR', dial: '55',  name: 'Brazil', flag: '🇧🇷' },
  { code: 'CA', dial: '1',   name: 'Canada', flag: '🇨🇦' },
  { code: 'CL', dial: '56',  name: 'Chile', flag: '🇨🇱' },
  { code: 'CN', dial: '86',  name: 'China', flag: '🇨🇳' },
  { code: 'CO', dial: '57',  name: 'Colombia', flag: '🇨🇴' },
  { code: 'CZ', dial: '420', name: 'Czechia', flag: '🇨🇿' },
  { code: 'DK', dial: '45',  name: 'Denmark', flag: '🇩🇰' },
  { code: 'FI', dial: '358', name: 'Finland', flag: '🇫🇮' },
  { code: 'FR', dial: '33',  name: 'France', flag: '🇫🇷' },
  { code: 'DE', dial: '49',  name: 'Germany', flag: '🇩🇪' },
  { code: 'GR', dial: '30',  name: 'Greece', flag: '🇬🇷' },
  { code: 'HK', dial: '852', name: 'Hong Kong', flag: '🇭🇰' },
  { code: 'IN', dial: '91',  name: 'India', flag: '🇮🇳' },
  { code: 'ID', dial: '62',  name: 'Indonesia', flag: '🇮🇩' },
  { code: 'IE', dial: '353', name: 'Ireland', flag: '🇮🇪' },
  { code: 'IL', dial: '972', name: 'Israel', flag: '🇮🇱' },
  { code: 'IT', dial: '39',  name: 'Italy', flag: '🇮🇹' },
  { code: 'JP', dial: '81',  name: 'Japan', flag: '🇯🇵' },
  { code: 'JO', dial: '962', name: 'Jordan', flag: '🇯🇴' },
  { code: 'KW', dial: '965', name: 'Kuwait', flag: '🇰🇼' },
  { code: 'LB', dial: '961', name: 'Lebanon', flag: '🇱🇧' },
  { code: 'MY', dial: '60',  name: 'Malaysia', flag: '🇲🇾' },
  { code: 'MX', dial: '52',  name: 'Mexico', flag: '🇲🇽' },
  { code: 'NL', dial: '31',  name: 'Netherlands', flag: '🇳🇱' },
  { code: 'NZ', dial: '64',  name: 'New Zealand', flag: '🇳🇿' },
  { code: 'NO', dial: '47',  name: 'Norway', flag: '🇳🇴' },
  { code: 'OM', dial: '968', name: 'Oman', flag: '🇴🇲' },
  { code: 'PK', dial: '92',  name: 'Pakistan', flag: '🇵🇰' },
  { code: 'PE', dial: '51',  name: 'Peru', flag: '🇵🇪' },
  { code: 'PH', dial: '63',  name: 'Philippines', flag: '🇵🇭' },
  { code: 'PL', dial: '48',  name: 'Poland', flag: '🇵🇱' },
  { code: 'PT', dial: '351', name: 'Portugal', flag: '🇵🇹' },
  { code: 'QA', dial: '974', name: 'Qatar', flag: '🇶🇦' },
  { code: 'RO', dial: '40',  name: 'Romania', flag: '🇷🇴' },
  { code: 'RU', dial: '7',   name: 'Russia', flag: '🇷🇺' },
  { code: 'SA', dial: '966', name: 'Saudi Arabia', flag: '🇸🇦' },
  { code: 'SG', dial: '65',  name: 'Singapore', flag: '🇸🇬' },
  { code: 'KR', dial: '82',  name: 'South Korea', flag: '🇰🇷' },
  { code: 'ES', dial: '34',  name: 'Spain', flag: '🇪🇸' },
  { code: 'SE', dial: '46',  name: 'Sweden', flag: '🇸🇪' },
  { code: 'CH', dial: '41',  name: 'Switzerland', flag: '🇨🇭' },
  { code: 'TH', dial: '66',  name: 'Thailand', flag: '🇹🇭' },
  { code: 'TR', dial: '90',  name: 'Türkiye', flag: '🇹🇷' },
  { code: 'AE', dial: '971', name: 'United Arab Emirates', flag: '🇦🇪' },
  { code: 'GB', dial: '44',  name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'US', dial: '1',   name: 'United States', flag: '🇺🇸' },
  { code: 'UA', dial: '380', name: 'Ukraine', flag: '🇺🇦' },
  { code: 'VN', dial: '84',  name: 'Vietnam', flag: '🇻🇳' }
]

/** Alphabetical by name, which is the order a picker should show. */
export const COUNTRIES_BY_NAME = [...COUNTRIES].sort((a, b) =>
  a.name.localeCompare(b.name)
)

const BY_CODE = new Map(COUNTRIES.map(c => [c.code, c]))

/** @param {string} code ISO 3166-1 alpha-2, e.g. 'NG' */
export function countryByCode (code) {
  return BY_CODE.get(String(code).toUpperCase()) ?? null
}

/**
 * The country a dialling code belongs to.
 *
 * Ambiguous by nature — +1 is both the US and Canada — so this returns the
 * first match and is only good enough for prefilling a picker, never for
 * deciding anything that matters.
 */
export function countryByDial (dial) {
  const digits = String(dial).replace(/\D/g, '')
  return COUNTRIES.find(c => c.dial === digits) ?? null
}
